/**
 * The tooling hook. ARCHITECTURE.md §18.
 *
 * Installs `window.__COI__`, the single surface every script in `tools/` drives the game through.
 * Tools never reach into internals, never scrape the DOM for state, and never depend on timing:
 * `runTicks` advances the simulation with no wall clock involved, so a capture cannot be affected
 * by how fast the machine happens to be.
 */

import { resetContextForMatch, hashFloat, hashInt, hashString, f60, clamp } from '../core/index.js';
import { forceState, PHASE, yawForFacing } from '../combat/index.js';
import { getScenario, SCENARIO_NAMES, BASELINE_SHOTS } from './scenarios.js';

function applyScenarioPoseFields(fighter, velocity, faceUp, knockdownHard) {
  if (velocity) {
    fighter.vx = Number(velocity[0]) || 0;
    fighter.vy = Number(velocity[1]) || 0;
    fighter.vz = Number(velocity[2]) || 0;
  }
  if (faceUp !== undefined) fighter.faceUp = !!faceUp;
  if (knockdownHard !== undefined) fighter.knockdownHard = !!knockdownHard;
}

/**
 * @param {object} ctx
 * @param {object} engine
 * @param {Promise<void>} readyPromise
 */
export function installTestHooks(ctx, engine, readyPromise) {
  if (typeof window === 'undefined') return null;

  const combat = ctx.systems.get('combat-sim');
  const physics = ctx.systems.get('physics');
  const input = ctx.systems.get('core-input');
  const render = ctx.systems.get('render');
  const fx = ctx.systems.get('fx');
  const audio = ctx.systems.get('audio');
  const ui = ctx.systems.get('ui');
  const overlay = ctx.systems.get('dev-overlay');

  const api = {
    // ---- determinism control -------------------------------------------------
    setSeed(n) {
      resetContextForMatch(ctx, n >>> 0);
      combat.startMatch(ctx);
      input.resetAll();
      return ctx.seed;
    },

    setDeterministic(on) {
      ctx.flags.deterministic = !!on;
      ctx.flags.pinnedQuality = !!on;
      ctx.time.deterministic = !!on;
      if (render) render.setPinned(!!on, ctx);
      if (on) {
        // Stop the rAF loop: a deterministic run is driven only by runTicks().
        engine.stop();
        ctx.config.set('dynamicResolution', false);
      }
      return ctx.flags.deterministic;
    },

    runTicks(n) {
      return engine.runDeterministic(Math.max(0, n | 0), false);
    },

    present() {
      engine.present();
      return ctx.time.frame;
    },

    tick() {
      return ctx.time.tick;
    },

    // ---- scene control -------------------------------------------------------
    setPreset(name) {
      ctx.config.set('preset', name);
      return ctx.render.preset;
    },

    setViewport(w, h, dpr) {
      engine.resize(w, h, dpr === undefined ? 1 : dpr);
      return { width: ctx.render.width, height: ctx.render.height, dpr: ctx.render.dpr };
    },

    setScreen(name) {
      ctx.app.screen = name;
      ctx.events.emit('ui:navigate', { tick: ctx.time.tick, screen: name, from: '' });
      combat.setTrainingMode(ctx, name === 'training');
      return ctx.app.screen;
    },

    startMatch(opts = {}) {
      const { p0, p1, mode, difficulty, seed, roundSeconds, bestOf } = opts;
      if (seed !== undefined) resetContextForMatch(ctx, seed >>> 0);
      if (roundSeconds !== undefined) ctx.config.set('roundSeconds', roundSeconds);
      if (bestOf !== undefined) ctx.config.set('bestOf', bestOf);
      if (mode) ctx.app.mode = mode;
      if (difficulty) {
        ctx.app.aiDifficulty = difficulty;
        ctx.config.set('aiDifficulty', difficulty);
      }
      if (p0 || p1) {
        ctx.app.selected = [p0 || ctx.app.selected[0], p1 || ctx.app.selected[1]];
        combat.setFighters(ctx, ctx.app.selected[0], ctx.app.selected[1]);
      } else {
        combat.startMatch(ctx);
      }
      input.resetAll();
      return api.state();
    },

    listScenarios() {
      return { all: SCENARIO_NAMES.slice(), baseline: BASELINE_SHOTS.slice() };
    },

    /**
     * Puts the game into an exact, reproducible state. See ARCHITECTURE.md §18.1.
     * Everything is applied through reset paths (`forceState`), never through gameplay
     * transitions, because a scenario reconstructs the world rather than playing into it.
     */
    setScenario(name) {
      const s = getScenario(name);
      resetContextForMatch(ctx, s.seed);
      if (s.preset) ctx.config.set('preset', s.preset);
      combat.setFighters(ctx, s.fighters[0], s.fighters[1]);
      api.setScreen(s.screen);
      input.resetAll();

      const [a, b] = ctx.sim.fighters;
      if (s.positions) {
        a.x = s.positions[0];
        b.x = s.positions[1];
      }
      if (s.depth !== undefined) b.z = s.depth;
      a.y = 0; b.y = 0;
      a.vx = 0; a.vy = 0; a.vz = 0;
      b.vx = 0; b.vy = 0; b.vz = 0;
      a.facing = a.x <= b.x ? 1 : -1;
      b.facing = -a.facing;
      a.yaw = yawForFacing(a.facing);
      b.yaw = yawForFacing(b.facing);
      a.prevX = a.x; a.prevY = a.y; a.prevZ = a.z; a.prevYaw = a.yaw;
      b.prevX = b.x; b.prevY = b.y; b.prevZ = b.z; b.prevYaw = b.yaw;

      if (s.p0Health !== undefined) a.health = Math.max(1, Math.round(a.maxHealth * s.p0Health));
      if (s.p1Health !== undefined) b.health = Math.max(0, Math.round(b.maxHealth * s.p1Health));

      // The match must not advance while a scenario is posed, or the timer would tick and a
      // KO would fire during the warm-up.
      ctx.sim.match.timerEnabled = false;
      ctx.sim.match.phase = s.screen === 'results' ? PHASE.MATCH_END : PHASE.FIGHT;
      if (s.rounds) {
        ctx.sim.match.rounds[0] = s.rounds[0];
        ctx.sim.match.rounds[1] = s.rounds[1];
      }
      if (s.bestCombo) {
        ctx.sim.match.bestCombo[0] = s.bestCombo[0];
        ctx.sim.match.bestCombo[1] = s.bestCombo[1];
      }
      if (s.bestComboDamage) {
        ctx.sim.match.bestComboDamage[0] = s.bestComboDamage[0];
        ctx.sim.match.bestComboDamage[1] = s.bestComboDamage[1];
      }
      if (s.timeRemaining !== undefined) ctx.sim.match.timerTicks = s.timeRemaining * 120;
      if (s.storm !== undefined) {
        ctx.sim.match.storm = s.storm;
        if (render && render.lighting) render.lighting.setStorm(s.storm);
      }

      // Warm up weather/particles/camera BEFORE freezing a move, so the frozen frame is not
      // contaminated by settling motion.
      if (s.warmupTicks) engine.runDeterministic(s.warmupTicks, false);

      // Fighters default to idle, not round-intro. `startMatch` leaves them in their intro pose
      // and only the INTRO -> FIGHT phase change releases them; a scenario sets the phase directly,
      // so without this every scenario that does not name a state would capture two fighters
      // standing in their entrance pose instead of fighting.
      forceState(a, s.p0State || 'idle', ctx, combat.emitter);
      forceState(b, s.p1State || 'idle', ctx, combat.emitter);
      if (s.p0StateTick !== undefined) a.stateTick = Math.max(0, s.p0StateTick | 0);
      if (s.p1StateTick !== undefined) b.stateTick = Math.max(0, s.p1StateTick | 0);
      applyScenarioPoseFields(a, s.p0Velocity, s.p0FaceUp, s.p0KnockdownHard);
      applyScenarioPoseFields(b, s.p1Velocity, s.p1FaceUp, s.p1KnockdownHard);
      if (s.p0Move) api.freezeMove(0, s.p0Move, s.p0Frame || 1);
      if (s.p1Move) api.freezeMove(1, s.p1Move, s.p1Frame || 1);
      if (s.pairedThrow) api.posePairedThrow(s.pairedThrow);

      if (overlay) overlay.setEnabled(!!s.hitboxes, ctx);

      // Clear every presentation transient BEFORE the capture frame. Without this a scenario
      // inherits the previous one's decaying impact lights and camera smoothing residual, and the
      // same scenario renders differently every time it is run — which would make
      // tools/imagediff.mjs meaningless.
      if (render) {
        if (render.camera) {
          if (s.camera) render.camera.lock(s.camera);
          else render.camera.unlock();
        }
        render.resetVisuals(ctx, s.storm === undefined ? -1 : s.storm);
      }
      // A frozen move does not run collision resolution, so it would not naturally emit the
      // impact event that belongs to its captured contact frame. Preview through the same bounded
      // spawn path used in live combat; this keeps critic shots honest without mutating gameplay.
      if (s.impact && fx) {
        fx.previewImpact(s.impact, ctx.time.tick - (s.impactAgeTicks || 0));
      }
      engine.present();
      return api.state();
    },

    /**
     * Poses a fighter mid-move at an exact f60 frame, without advancing the simulation.
     * This is how a capture shows an active frame: the move is placed, not played into.
     */
    freezeMove(player, moveId, frame60) {
      const f = ctx.sim.fighters[player];
      const move = ctx.data.moves.byId.get(moveId);
      if (!move) throw new Error(`Unknown move "${moveId}"`);
      f.move = move;
      f.moveId = move.id;
      f.moveTick = clamp(f60(frame60), 0, move.tTotal - 1);
      f.moveHasHit = false;
      f.hitboxUsed.fill(0);
      f.hitstunTicks = 0;
      f.blockstunTicks = 0;
      f.frozenTicks = 0;
      const state = f.moveTick < move.tStartup ? 'attack-startup'
        : f.moveTick < move.tStartup + move.tActive ? 'attack-active'
        : 'attack-recovery';
      forceState(f, move.isThrow ? 'throw-attempt' : state, ctx, combat.emitter);
      // Rebuild the volumes so the overlay shows this exact frame.
      physics.buildDefensiveVolumes(ctx);
      physics.buildOffensiveVolumes(ctx);
      return { moveId, frame: frame60, state: f.state };
    },

    /**
     * Reconstructs the same linked fields combat owns after a throw connects. Presentation critic
     * shots can therefore inspect a true paired hold/execute pose without fabricating damage.
     */
    posePairedThrow(spec) {
      const attackerIndex = spec && spec.attacker === 1 ? 1 : 0;
      const victimIndex = 1 - attackerIndex;
      const attacker = ctx.sim.fighters[attackerIndex];
      const victim = ctx.sim.fighters[victimIndex];
      const move = ctx.data.moves.byId.get(spec && spec.moveId);
      if (!move || !move.isThrow) throw new Error(`Unknown paired throw "${spec && spec.moveId}"`);
      const state = spec.state === 'execute' ? 'throw-execute' : 'throw-hold';
      const tick = Math.max(0, spec.tick | 0);

      if (spec.state === 'release') {
        attacker.move = null;
        attacker.moveId = '';
        attacker.throwPartner = -1;
        attacker.throwMoveId = '';
        attacker.throwTick = 0;
        attacker.throwChainIndex = 0;
        forceState(attacker, 'idle', ctx, combat.emitter);
        attacker.prevState = 'throw-execute';
        attacker.stateTick = tick;
        const record = attacker.actionLog[attacker.actionLogHead];
        record.tick = ctx.time.tick - tick;
        record.moveId = move.id;
        record.outcome = 'end';
        record.state = 'throw-execute';
        attacker.actionLogHead = (attacker.actionLogHead + 1) % attacker.actionLog.length;

        victim.move = null;
        victim.moveId = '';
        victim.throwPartner = -1;
        victim.throwMoveId = '';
        victim.throwTick = 0;
        victim.throwBreakTicks = 0;
        forceState(victim, 'knockdown-fall', ctx, combat.emitter);
        victim.prevState = 'thrown';
        victim.stateTick = tick;
        const offset = (attacker.stats.pushboxRadius + victim.stats.pushboxRadius) * 0.82;
        const away = attacker.facing;
        const elapsed = tick / 120;
        victim.x = attacker.x + attacker.facing * offset + away * 4.2 * elapsed;
        victim.y = Math.max(0, 3 * elapsed - 4.9 * elapsed * elapsed);
        victim.z = attacker.z;
        victim.vx = away * 4.2;
        victim.vy = Math.max(0, 3 - 9.8 * elapsed);
        victim.vz = 0;
        victim.facing = -attacker.facing;
        victim.yaw = yawForFacing(victim.facing);
        victim.prevX = victim.x;
        victim.prevY = victim.y;
        victim.prevZ = victim.z;
        victim.prevYaw = victim.yaw;
        physics.buildDefensiveVolumes(ctx);
        physics.buildOffensiveVolumes(ctx);
        return {
          attacker: attackerIndex, victim: victimIndex, moveId: move.id, state: 'release', tick,
        };
      }

      attacker.move = move;
      attacker.moveId = move.id;
      attacker.moveTick = move.tStartup;
      attacker.throwPartner = victimIndex;
      attacker.throwMoveId = move.id;
      attacker.throwTick = tick;
      attacker.throwChainIndex = Math.max(0, spec.chainIndex | 0);
      forceState(attacker, state, ctx, combat.emitter);
      attacker.stateTick = tick;

      victim.move = null;
      victim.moveId = '';
      victim.throwPartner = attackerIndex;
      victim.throwMoveId = '';
      victim.throwTick = 0;
      victim.throwBreakTicks = state === 'throw-hold' ? Math.max(0, move.tBlockstun - tick) : 0;
      forceState(victim, 'thrown', ctx, combat.emitter);
      victim.stateTick = tick;

      const offset = (attacker.stats.pushboxRadius + victim.stats.pushboxRadius) * 0.82;
      victim.x = attacker.x + attacker.facing * offset;
      victim.z = attacker.z;
      victim.facing = -attacker.facing;
      victim.yaw = yawForFacing(victim.facing);
      victim.prevX = victim.x;
      victim.prevY = victim.y;
      victim.prevZ = victim.z;
      victim.prevYaw = victim.yaw;
      physics.buildDefensiveVolumes(ctx);
      physics.buildOffensiveVolumes(ctx);
      return { attacker: attackerIndex, victim: victimIndex, moveId: move.id, state, tick };
    },

    // ---- input injection -----------------------------------------------------
    setInput(player, state) {
      return input.setSyntheticInput(player, state || {});
    },

    patchInput(player, state) {
      return input.patchSyntheticInput(player, state || {});
    },

    /** Presses `keys` for `ticks` ticks, advancing the simulation, then releases. */
    pressInput(player, keys, ticks = 4) {
      const state = {};
      for (const k of keys) state[k] = true;
      input.setSyntheticInput(player, state);
      engine.runDeterministic(ticks, false);
      input.setSyntheticInput(player, {});
      return ctx.time.tick;
    },

    clearInput(player) {
      input.clearSynthetic(player);
      return true;
    },

    /**
     * Replays a frame-by-frame input stream. `frames` is an array of `[p0, p1]` where each entry
     * is a partial action map. Returns the per-tick checksum trail so divergence can be located.
     */
    playReplay(frames) {
      const trail = [];
      for (let i = 0; i < frames.length; i++) {
        const pair = frames[i] || [];
        input.setSyntheticInput(0, pair[0] || {});
        input.setSyntheticInput(1, pair[1] || {});
        engine.runDeterministic(1, false);
        trail.push(api.checksum());
      }
      return trail;
    },

    // ---- observation ---------------------------------------------------------
    state() {
      const m = ctx.sim.match;
      return {
        tick: ctx.time.tick,
        frame: ctx.time.frame,
        seed: ctx.seed,
        screen: ctx.app.screen,
        preset: ctx.render.preset,
        renderScale: ctx.render.renderScale,
        exposure: +ctx.render.exposure.toFixed(4),
        match: m ? {
          phase: m.phase, round: m.round, bestOf: m.bestOf, rounds: [...m.rounds],
          timerTicks: m.timerTicks, storm: +m.storm.toFixed(4),
        } : null,
        gap: +physics.gap(ctx).toFixed(4),
        camera: render && render.camera ? render.camera.state() : null,
        fighters: ctx.sim.fighters.map((f) => ({
          id: f.id,
          x: +f.x.toFixed(5), y: +f.y.toFixed(5), z: +f.z.toFixed(5),
          vx: +f.vx.toFixed(5), vy: +f.vy.toFixed(5), vz: +f.vz.toFixed(5),
          facing: f.facing, state: f.state, stateTick: f.stateTick, poseTick: f.poseTick,
          moveId: f.moveId, moveTick: f.moveTick,
          health: f.health, maxHealth: f.maxHealth,
          meter: +f.meter.toFixed(3), stance: f.stance,
          comboHits: f.comboHits, comboDamage: f.comboDamage,
          comboScale: +f.comboScale.toFixed(4),
          posture: f.posture, hitstun: f.hitstunTicks, blockstun: f.blockstunTicks,
          frozen: f.frozenTicks,
        })),
      };
    },

    /** FNV-1a digest of gameplay-relevant state. ARCHITECTURE.md §13. */
    checksum() {
      let h = 0x811c9dc5;
      for (const f of ctx.sim.fighters) {
        h = hashFloat(h, f.x); h = hashFloat(h, f.y); h = hashFloat(h, f.z);
        h = hashFloat(h, f.vx); h = hashFloat(h, f.vy); h = hashFloat(h, f.vz);
        h = hashString(h, f.state); h = hashInt(h, f.stateTick);
        h = hashInt(h, f.poseTick);
        h = hashString(h, f.moveId); h = hashInt(h, f.moveTick);
        h = hashFloat(h, f.health); h = hashInt(h, f.comboHits);
        h = hashFloat(h, f.comboScale); h = hashInt(h, f.facing);
        h = hashFloat(h, f.meter);
        h = hashInt(h, f.hitstunTicks); h = hashInt(h, f.blockstunTicks);
      }
      const m = ctx.sim.match;
      h = hashInt(h, m.timerTicks); h = hashInt(h, m.round);
      h = hashInt(h, m.rounds[0]); h = hashInt(h, m.rounds[1]);
      h = hashString(h, m.phase);
      h = hashInt(h, ctx.rng.sim.digest());
      h = hashInt(h, ctx.rng.ai.digest());
      return h >>> 0;
    },

    metrics() {
      return ctx.metrics.snapshot();
    },

    poolStats() {
      return { pools: ctx.pools.stats(), allBounded: ctx.pools.allBounded() };
    },

    rendererInfo() {
      return render ? render.rendererInfo() : null;
    },

    shaderStats() {
      const info = render ? render.rendererInfo() : null;
      return {
        programs: info ? info.programs : 0,
        lateCompiles: ctx.metrics.getCounter('render.lateShaderCompiles'),
        compilesDuringCombat: render ? render.shaderCompilesDuringCombat : 0,
      };
    },

    events(sinceTick = 0) {
      return ctx.events.historyList().filter((e) => e.tick >= sinceTick);
    },

    frameData(moveId) {
      const m = ctx.data.moves.byId.get(moveId);
      if (!m) return null;
      return {
        id: m.id, name: m.name, input: m.input, level: m.level,
        startup: m.startup, active: m.active, recovery: m.recovery,
        damage: m.damage, hitstun: m.hitstun, blockstun: m.blockstun,
        hitAdvantage: m.hitAdvantage, blockAdvantage: m.blockAdvantage,
        launcher: m.onHit.launch > 0, wallSplat: m.onHit.wallSplat,
        anim: m.anim, tags: m.tags.slice(),
      };
    },

    aiTelemetry() {
      const ai = ctx.systems.get('ai');
      return ai && ai.telemetry ? ai.telemetry() : null;
    },

    uiState() {
      return ui && ui.state ? ui.state() : null;
    },

    setTimeScale(s) {
      ctx.time.timeScale = s;
      return ctx.time.timeScale;
    },

    pause() {
      combat.pause(ctx);
      return true;
    },

    resume() {
      combat.resume(ctx);
      return true;
    },

    /** Enables/disables the collision overlay. */
    setHitboxes(on) {
      if (overlay) overlay.setEnabled(!!on, ctx);
      return !!on;
    },

    /** Poses one real pooled impact without mutating combat, for FX critic captures. */
    previewImpact(spec) {
      if (!fx) return false;
      fx.resetVisuals();
      fx.previewImpact(spec || {}, ctx.time.tick);
      engine.present();
      return true;
    },

    audioState() {
      return audio && audio.state ? audio.state() : null;
    },

    /** Emits one canonical audio cue for browser graph verification. */
    previewAudioCue(cue, opts = {}) {
      ctx.events.emit('audio:cue', {
        tick: ctx.time.tick,
        cue,
        x: opts.x || 0,
        y: opts.y || 1,
        z: opts.z || 0,
        gain: opts.gain === undefined ? 1 : opts.gain,
        pitch: opts.pitch === undefined ? 1 : opts.pitch,
        variant: opts.variant || 0,
        surface: opts.surface || '',
      });
      return audio && audio.state ? audio.state() : null;
    },
  };

  const hook = { ready: readyPromise, ctx, engine, api };
  window.__COI__ = hook;
  ctx.log.info('dev: window.__COI__ installed');
  return hook;
}
